import { Link } from 'react-router-dom';
import { Heart, Leaf, Users, Award, ArrowRight } from 'lucide-react';

export default function About() {
  return (
    <div className="pt-20 min-h-screen bg-cream" data-testid="about-page">
      {/* Hero */}
      <section className="py-16 bg-dark relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img
            src="https://images.unsplash.com/photo-1760562796021-d8bbcb5bc318?w=1920"
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        <div className="container-custom relative z-10">
          <h1 className="font-playfair text-4xl md:text-5xl font-bold text-cream mb-4" data-testid="about-title">
            About FarmBox
          </h1>
          <p className="font-manrope text-cream/70 text-lg max-w-xl">
            Connecting the White Mountain community with the freshest local food.
          </p>
        </div>
      </section>

      {/* Mission */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-xs font-manrope uppercase tracking-[0.2em] font-bold text-forest mb-4 block">Our Mission</span>
              <h2 className="font-playfair text-4xl font-bold text-dark mb-6">
                Farm Fresh Food for Every Table
              </h2>
              <p className="font-manrope text-dark/70 leading-relaxed mb-6">
                FarmBox was created with one mission: connect the White Mountain community with the freshest local food possible. We partner directly with small farms across New Hampshire to bring you peak-fresh produce, dairy, meat, and seasonal boxes, all delivered straight to your door.
              </p>
              <p className="font-manrope text-dark/70 leading-relaxed mb-8">
                When you choose FarmBox, you're choosing better food and a better future. You're supporting local families and preserving New Hampshire's agricultural heritage for future generations.
              </p>
              <Link to="/farms" className="btn-primary" data-testid="about-farms-btn">
                Meet Our Farms
                <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
            </div>
            <div className="relative">
              <img
                src="https://images.pexels.com/photos/3978831/pexels-photo-3978831.jpeg?w=800"
                alt="Person holding fresh vegetables"
                className="rounded-[2rem] w-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="text-xs font-manrope uppercase tracking-[0.2em] font-bold text-forest mb-4 block">What We Believe</span>
            <h2 className="font-playfair text-4xl font-bold text-dark">Our Values</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: Leaf,
                title: 'Local First',
                description: 'Every product comes from farms within the White Mountain Region.'
              },
              {
                icon: Heart,
                title: 'Sustainability',
                description: 'We partner with farms that prioritize environmental stewardship.'
              },
              {
                icon: Users,
                title: 'Community',
                description: 'Supporting local farmers strengthens our entire community.'
              },
              {
                icon: Award,
                title: 'Quality',
                description: 'Only the freshest, highest-quality products make it into your box.'
              }
            ].map((value, index) => (
              <div key={index} className="text-center p-8">
                <div className="w-16 h-16 bg-forest/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <value.icon className="w-8 h-8 text-forest" />
                </div>
                <h3 className="font-playfair text-xl font-bold text-dark mb-3">{value.title}</h3>
                <p className="font-manrope text-dark/60">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="section-padding bg-forest relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-gold rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-cream rounded-full blur-3xl"></div>
        </div>
        <div className="container-custom relative z-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { value: 'New', label: 'Farm Partners Joining Soon' },
              { value: '100%', label: 'Local Products' },
              { value: 'Farm-Direct', label: 'Sourcing, No Middlemen' },
              { value: 'Coös County', label: 'Proudly Serving' }
            ].map((stat, index) => (
              <div key={index}>
                <p className="font-playfair text-5xl font-bold text-cream mb-2">{stat.value}</p>
                <p className="font-manrope text-cream/70">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-cream">
        <div className="container-custom text-center">
          <h2 className="font-playfair text-4xl font-bold text-dark mb-6">
            Ready to Taste the Difference?
          </h2>
          <p className="font-manrope text-dark/70 text-lg mb-8 max-w-xl mx-auto">
            Join the FarmBox family and experience the freshest local food the White Mountains has to offer.
          </p>
          <Link to="/shop" className="btn-primary" data-testid="about-shop-btn">
            Start Shopping
            <ArrowRight className="w-5 h-5 ml-2" />
          </Link>
        </div>
      </section>
    </div>
  );
}
